<script setup lang="ts">
  import { getCurrentInstance, ref, watch } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import { useMenuImport } from '@/composition/menu-import/menu-import-context';
  import type {
    ILiveLineAction,
    ILiveLineModel,
  } from '@/domain/menu-import/live-rows';

  /**
   * The amber line of a dish of an update (plan §3.5 «The live line»): a
   * dish that may be one of the menu («¿Es «Ravioles» de su menú?»), or a
   * price we kept until the owner decides («Revise: el precio baja…»), with
   * the buttons that settle it. Used by the dish row and the review queue.
   * A tapped button waits (all of them off) until the answer changes the
   * line or the session has nothing left to send.
   */
  const props = defineProps<{
    model: ILiveLineModel;
  }>();
  const emit = defineEmits<{
    (e: 'action', action: ILiveLineAction): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const ctx = useMenuImport();
  /** A decision was sent and its answer did not arrive yet. */
  const waiting = ref(false);

  watch(
    () => props.model,
    () => {
      waiting.value = false;
    }
  );
  // Refused or failed: the queue emptied without changing the line.
  watch(
    () => ctx.pending.value,
    (count) => {
      if (count === 0) waiting.value = false;
    }
  );

  function choose(action: ILiveLineAction) {
    if (waiting.value) return;
    waiting.value = true;
    emit('action', action);
    // The handlers queue their write at once: nothing queued, nothing to wait for.
    if (ctx.pending.value === 0) waiting.value = false;
  }

  /** A ready-made label (a candidate), else the translated key. */
  function actionText(action: ILiveLineAction): string {
    if (action.label !== null) return action.label;
    return action.key === null
      ? ''
      : String(proxy.$t(action.key, action.params));
  }

  function actionKey(action: ILiveLineAction, index: number): string {
    return `${action.kind}-${action.liveItemId ?? index}`;
  }
</script>

<template>
  <div class="v2-menu-live-line">
    <p class="line-text">
      <PosUiIcon name="alert-circle-outline" class="line-icon" />
      <span>{{ $t(model.textKey, model.params) }}</span>
    </p>
    <div class="line-actions">
      <PosUiButton
        v-for="(action, index) in model.actions"
        :key="actionKey(action, index)"
        color="neutral"
        variant="outline"
        size="sm"
        class="line-action"
        :class="{ 'is-candidate': action.label !== null }"
        :disabled="waiting"
        @click="choose(action)"
        >{{ actionText(action) }}</PosUiButton
      >
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-menu-live-line {
    @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-2 tw-text-sm tw-text-amber-700;

    .line-text {
      @apply tw-m-0 tw-flex tw-items-start tw-gap-2;
    }
    .line-icon {
      @apply tw-mt-0.5 tw-shrink-0 tw-text-base;
    }
    .line-actions {
      @apply tw-flex tw-flex-wrap tw-gap-2;
    }
    // Touch first: 44px on phones, the compact size from tablets.
    .line-action {
      @apply tw-min-h-11 md:tw-min-h-0;
      // A candidate («Ravioles · $ 11.900») may be long: it wraps, full width on phones.
      &.is-candidate {
        @apply tw-h-auto tw-w-full tw-whitespace-normal tw-py-2 tw-text-left md:tw-w-auto;
      }
    }
  }
</style>
